// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseSuperAdminFactory instead of class inheritance

import { createBaseSuperAdmin, type IBaseSuperAdmin } from "../BaseSuperAdminFactory";
import type { Customer } from "le-coffre-resources/dist/SuperAdmin";
import type { ApiPayload, QueryPayload } from "../../../types";
import type { IGetCustomersparams, IPutCustomersParams } from "./Customers";
import {
	buildCustomersCreatePayload,
	buildCustomersUpdatePayload,
} from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

interface CustomersInstance {
	get(q: IGetCustomersparams): Promise<Customer[]>;
	post(body: ApiPayload): Promise<Customer>;
	getByUid(uid: string, q?: QueryPayload): Promise<Customer>;
	put(uid: string, body: IPutCustomersParams): Promise<Customer>;
}

// Module-level singleton instance
let instance: CustomersInstance | null = null;
let baseSuperAdminInstance: IBaseSuperAdmin | null = null;

async function customersGet(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, q: IGetCustomersparams): Promise<Customer[]> {
	const url = new URL(baseUrl);
	baseSuperAdmin.appendJsonSearchParams(url, { q });
	try {
		return await baseSuperAdmin.getRequest<Customer[]>(url);
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function customersPost(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, body: ApiPayload): Promise<Customer> {
	const url = new URL(baseUrl);
	try {
		return await baseSuperAdmin.postRequest<Customer>(url, buildCustomersCreatePayload(body));
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function customersGetByUid(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, uid: string, q?: QueryPayload): Promise<Customer> {
	const url = new URL(baseUrl.concat(`/${uid}`));
	if (q) baseSuperAdmin.appendJsonSearchParams(url, { q });
	try {
		return await baseSuperAdmin.getRequest<Customer>(url);
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

async function customersPut(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string, uid: string, body: IPutCustomersParams): Promise<Customer> {
	const url = new URL(baseUrl.concat(`/${uid}`));
	try {
		return await baseSuperAdmin.putRequest<Customer>(url, buildCustomersUpdatePayload(body));
	} catch (err) {
		baseSuperAdmin.onError(err);
		return Promise.reject(err);
	}
}

function createCustomers(): CustomersInstance {
	if (!baseSuperAdminInstance) {
		baseSuperAdminInstance = createBaseSuperAdmin();
	}
	const baseSuperAdmin = baseSuperAdminInstance;
	const baseUrl = baseSuperAdmin.namespaceUrl.concat("/customers");
	return {
		get: (q: IGetCustomersparams) => customersGet(baseSuperAdmin, baseUrl, q),
		post: (body: ApiPayload) => customersPost(baseSuperAdmin, baseUrl, body),
		getByUid: (uid: string, q?: QueryPayload) => customersGetByUid(baseSuperAdmin, baseUrl, uid, q),
		put: (uid: string, body: IPutCustomersParams) => customersPut(baseSuperAdmin, baseUrl, uid, body),
	};
}

export function getCustomersInstance(): CustomersInstance {
	if (!instance) {
		instance = createCustomers();
	}
	return instance;
}
